// Gemeinsame Hilfen für Branchenvorlagen (Galerie, Schnellstart, Folie ausfüllen).
import type { LayoutV1, LayoutV2, WeatherProvider } from '@disploraq/layouts';
import type { I18nText, LayoutRow, TemplateCatalog, TemplateCatalogItem } from '../../api/types';

/** Beispielwetter für die Galerie (der Player bekommt echte Daten vom Server). */
export const sampleWeather: WeatherProvider = async () => {
  const day = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);
  return {
    current: { temperature_2m: 18, weather_code: 1 },
    daily: { time: [day(0), day(1), day(2)], weather_code: [1, 3, 61], temperature_2m_max: [19, 16, 13], temperature_2m_min: [9, 8, 7] },
  };
};

export const tr = (t: I18nText, lang: string): string => (lang.startsWith('en') ? t.en : t.de);

const specOf = (def: unknown): number | undefined => (def as { specVersion?: number } | null)?.specVersion;

/** Layout mit Formular (M16): Zonenvorlage (v1) oder freie Folie mit Feldern (v2). */
export function fillable(l: LayoutRow | undefined): LayoutV1 | LayoutV2 | null {
  if (!l) return null;
  const d = l.definition as LayoutV1 | LayoutV2;
  if (specOf(d) === 1) return d as LayoutV1;
  if (specOf(d) === 2 && ((d as LayoutV2).fields?.length ?? 0) > 0) return d as LayoutV2;
  return null;
}

/** Gruppe (16:9 + 9:16) einer Branchenvorlage. */
export interface TemplateGroup {
  group: string;
  category: string;
  name: I18nText;
  description: I18nText;
  tags: string[];
  sort: number;
  variants: Partial<Record<'landscape' | 'portrait', TemplateCatalogItem>>;
}

export function groupCatalog(cat: TemplateCatalog | undefined): TemplateGroup[] {
  const map = new Map<string, TemplateGroup>();
  for (const it of cat?.items ?? []) {
    let g = map.get(it.group);
    if (!g) {
      g = { group: it.group, category: it.category, name: it.name, description: it.description, tags: it.tags, sort: it.sort, variants: {} };
      map.set(it.group, g);
    }
    g.variants[it.orientation] = it;
    g.sort = Math.min(g.sort, it.sort);
  }
  return [...map.values()].sort((a, b) => a.sort - b.sort);
}

/** Suche in Name, Beschreibung, Schlagworten und Kategorie (beide Sprachen, ohne Gross/Klein und Umlaute). */
export function matches(g: TemplateGroup, q: string, categoryName = ''): boolean {
  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const words = norm(q).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = norm([g.name.de, g.name.en, g.description.de, g.description.en, ...g.tags, categoryName, g.category].join(' '));
  return words.every((w) => hay.includes(w));
}
